import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogActions, DialogContent, Toolbar, Typography, Button, CircularProgress } from '@mui/material';
import { Form, Field } from 'react-final-form';
import Config from '../../config';
import { ShippingLabelStatus } from './shippingLabelStatus';
import DropZone from '../files/dropzone';
import DownloadButton from '../../components/download_button';
import ExtendedField from '../../components/text_field';

interface ShippingLabelDialogProps {
    open: boolean;
    onClose: () => void;
    integrationId?: number | string;
    // A null/empty status means no label has been uploaded yet.
    status?: string | null;
    // Suppliers upload labels; retailers approve or reject them.
    isRetailer?: boolean;
    canApprove?: boolean;
    canReject?: boolean;
    // Called after a successful upload, approval or rejection so the caller can reload the status.
    onChange?: () => void | Promise<void>;
}

const getErrorMessage = (e: any) => (e && e.response && e.response.data && e.response.data.error) || 'Unexpected error.';

const ShippingLabelDialog = ({ open, onClose, integrationId, status, isRetailer, canApprove, canReject, onChange }: ShippingLabelDialogProps) => {
    const [labelUrl, setLabelUrl] = useState<string | null>(null);
    const [loadingLabel, setLoadingLabel] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showReject, setShowReject] = useState(false);
    const [error, setError] = useState('');
    const [updating, setUpdating] = useState(false);
    const hasLabel = !!status;
    // Suppliers see the current label first and must choose Update to replace it.
    const showDropZone = !isRetailer && (!hasLabel || updating);

    useEffect(() => {
        if (!open || !hasLabel || !integrationId) {
            return undefined;
        }
        let cancelled = false;
        let url: string | null = null;
        const loadLabel = async () => {
            setLoadingLabel(true);
            setLoadFailed(false);
            try {
                const response = await axios.get(Config.oauth + '/integration/GetShippingLabel/' + integrationId, { responseType: 'blob' });
                if (cancelled) {
                    return;
                }
                url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                setLabelUrl(url);
            } catch (e) {
                if (!cancelled) {
                    setLoadFailed(true);
                }
            } finally {
                if (!cancelled) {
                    setLoadingLabel(false);
                }
            }
        };
        loadLabel();
        return () => {
            cancelled = true;
            if (url) {
                URL.revokeObjectURL(url);
            }
            setLabelUrl(null);
        };
    }, [open, hasLabel, integrationId, status]);

    useEffect(() => {
        if (!open) {
            setFile(null);
            setShowReject(false);
            setUpdating(false);
        }
    }, [open]);

    const runAction = useCallback(async (action: () => Promise<unknown>) => {
        setIsSubmitting(true);
        try {
            await action();
            setIsSubmitting(false);
            setShowReject(false);
            onClose();
            if (onChange) {
                await onChange();
            }
        } catch (e) {
            setIsSubmitting(false);
            setError(getErrorMessage(e));
        }
    }, [onChange, onClose]);

    const save = () => runAction(() => {
        const formData = new FormData();
        formData.append('file', file as File);
        return axios.post(Config.oauth + '/integration/SetShippingLabel/' + integrationId, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    });
    const approve = () => runAction(() => axios.post(Config.oauth + '/integration/OperatorAcceptShippingLabel/' + integrationId));
    const reject = (values: any) => runAction(() => axios.post(Config.oauth + '/integration/OperatorRejectShippingLabel/' + integrationId, values));

    const labelLoaded = !!labelUrl;
    let labelView: React.ReactNode;
    if (hasLabel && loadingLabel) {
        labelView = <div style={{ height: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></div>;
    } else if (labelLoaded) {
        labelView = <iframe title='Shipping label' src={labelUrl as string} style={{ display: 'block', boxSizing: 'border-box', width: '100%', height: '60vh', border: '1px solid #e0e0e0', borderRadius: '4px' }} />;
    } else if (isRetailer || loadFailed) {
        labelView = <div style={{ boxSizing: 'border-box', height: '10em', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f6f6f6', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <Typography color='textSecondary'>{loadFailed ? 'The shipping label could not be loaded.' : 'No shipping label has been uploaded yet.'}</Typography>
        </div>;
    }

    const dropZone = <DropZone accept='.pdf' style={{ width: '100%', height: '10em' }} onDrop={(files: File[]) => { setFile(files[0] || null); }}>
        {({ isDragAccept, isDragReject }: any) => {
            if (isDragAccept || isDragReject) {
                return 'Drop here to upload this file.';
            }
            return file ? file.name : 'Drop a PDF file here or click to choose a file.';
        }}
    </DropZone>;

    return <>
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
            <Toolbar className='lbtoolbar'>Shipping Label</Toolbar>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
                {!showDropZone && labelView}
                {showDropZone && dropZone}
            </DialogContent>
            <DialogActions>
                {isRetailer ? <>
                    <Button variant='outlined' onClick={() => setShowReject(true)} disabled={!labelLoaded || !canReject || isSubmitting}>Reject</Button>
                    <DownloadButton variant='contained' color='primary' onClick={approve} disabled={!labelLoaded || !canApprove || isSubmitting}>Approve</DownloadButton>
                </> : showDropZone
                    ? <DownloadButton variant='contained' color='primary' onClick={save} disabled={!file || isSubmitting}>Save</DownloadButton>
                    : status !== ShippingLabelStatus.Complete && <Button variant='contained' color='primary' onClick={() => setUpdating(true)}>Update</Button>}
            </DialogActions>
        </Dialog>
        <Dialog open={showReject} onClose={() => setShowReject(false)} fullWidth maxWidth='xs'>
            <Toolbar className='lbtoolbar'>Reject Shipping Label</Toolbar>
            <Form onSubmit={reject}
                render={({ handleSubmit, pristine, invalid }) => (
                    <form autoComplete='off' onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <DialogContent>
                            <div style={{ display: 'flex', flex: '1 1 auto', position: 'relative' }}>
                                <Field style={{ width: '100%' }} validate={v => v ? undefined : 'Reason is required.'} label='Rejection reason' component={ExtendedField} name='RejectionReason' />
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <DownloadButton color='primary' onClick={handleSubmit} type='submit' disabled={pristine || invalid || isSubmitting}>Reject</DownloadButton>
                        </DialogActions>
                    </form>
                )} />
        </Dialog>
        <Dialog open={!!error} onClose={() => setError('')}>
            <Toolbar className='lbtoolbar'>Error</Toolbar>
            <DialogContent><Typography>{error}</Typography></DialogContent>
        </Dialog>
    </>;
};

export default ShippingLabelDialog;
